import { supabase } from '@/integrations/supabase/client';

export interface TemplateType {
  id: string;
  name: string;
}

const CACHE_KEY = 'template_types_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Page types to exclude from content generation
const EXCLUDED_PAGE_TYPES = ['feature_page', 'landing_page'];

interface CacheData {
  timestamp: number;
  data: TemplateType[];
}

export const fetchTemplateTypes = async (): Promise<TemplateType[]> => {
  try {
    // Check cache first
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const { timestamp, data } = JSON.parse(cachedData) as CacheData;
      if (Date.now() - timestamp < CACHE_DURATION) {
        // Filter out page types from cached data
        return data.filter(type => !EXCLUDED_PAGE_TYPES.includes(type.id));
      }
    }

    // Fetch from database if cache is invalid or missing
    const { data, error } = await supabase
      .from('template_types')
      .select('id, name')
      .order('name');

    if (error) {
      console.error('Error fetching template types:', error);
      throw error;
    }

    // Filter out page types
    const filteredData = (data || []).filter(type => !EXCLUDED_PAGE_TYPES.includes(type.id));

    // Cache the filtered results
    const cacheData: CacheData = {
      timestamp: Date.now(),
      data: filteredData
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

    return filteredData;
  } catch (error) {
    console.error('Error in fetchTemplateTypes:', error);
    // Return default types if fetch fails (excluding page types)
    return [
      { id: 'guide', name: 'Guide' },
      { id: 'comparison', name: 'Comparison' },
      { id: 'list', name: 'List' },
      { id: 'press_release', name: 'Press Release' }
    ];
  }
};

export const clearTemplateTypesCache = () => {
  localStorage.removeItem(CACHE_KEY);
};